import { customerMarketShopfrontApi } from '@envato/foundation-types'
import { match } from 'react-router-dom'
import { ThunkAction } from 'redux-thunk'
import uniq from 'lodash-es/uniq'

import {
  setSeoMeta,
  transformBlocksToArray,
  reportBlocks,
  getItemIdsFromBlocks
} from './common'
import { ICategoryPageResult } from '../reducers/categoryPage'
import { reportPageType } from '../analytics'
import { getBookmarksForItems } from './user/bookmark'
import { IRootState } from '../reducers'
import axiosVerbose from '../utils/axios/axiosVerbose'
import { logError } from '../utils/errors'
import { ICommonLocation, ILocationParams } from '../utils/location/types'
import { getSearchLocationParams } from '../utils/location'
import { selectBlocksFromActiveFeatureFlip } from '../../common/utils/pageBlocks'

export enum TypeKeys {
  GET_CATEGORY_PAGE_API_REQUEST = 'GET_CATEGORY_PAGE_API_REQUEST',
  GET_CATEGORY_PAGE_API_SUCCESS = 'GET_CATEGORY_PAGE_API_SUCCESS',
  GET_CATEGORY_PAGE_API_FAIL = 'GET_CATEGORY_PAGE_API_FAIL'
}

export interface IGetCategoryPageApiRequestAction {
  type: TypeKeys.GET_CATEGORY_PAGE_API_REQUEST
}

export interface IGetCategoryPageApiSuccessAction {
  type: TypeKeys.GET_CATEGORY_PAGE_API_SUCCESS
  payload: Readonly<ICategoryPageResult>
}

export interface IGetCategoryPageApiFailAction {
  type: TypeKeys.GET_CATEGORY_PAGE_API_FAIL
}

export type ActionTypes =
  | IGetCategoryPageApiRequestAction
  | IGetCategoryPageApiSuccessAction
  | IGetCategoryPageApiFailAction

export const onBeforeCategoryPageView = ({
  routeMatch
}: {
  routeMatch: match<any>
  prevLocation?: ICommonLocation
}): ThunkAction<void, IRootState, void> => (dispatch, getState) => {
  const currentSite = getState().common.currentSite
  const location = getState().router.location
  const experiments = getState().experiments
  const { blocks, blocksB, blocksC, blocksD } = getState().categoryPage

  const locationParams = getSearchLocationParams({
    location,
    routeMatch,
    currentSite
  })

  const selectedBlocks = selectBlocksFromActiveFeatureFlip({
    page: 'category',
    data: { blocks, blocksB, blocksC, blocksD },
    location,
    currentSite,
    experiments
  })

  reportPageType('category', currentSite)

  reportBlocks({
    blocks: selectedBlocks,
    currentSite,
    page: 'Category Page',
    category: locationParams.category || 'all'
  })
}

export const fetchCategoryPageDataRequest = () => {
  const action: IGetCategoryPageApiRequestAction = {
    type: TypeKeys.GET_CATEGORY_PAGE_API_REQUEST
  }

  return action
}

export const fetchCategoryPageDataSuccess = (payload: any) => {
  const action: IGetCategoryPageApiSuccessAction = {
    payload,
    type: TypeKeys.GET_CATEGORY_PAGE_API_SUCCESS
  }

  return action
}

export const fetchCategoryPageDataFail = () => {
  const action: IGetCategoryPageApiFailAction = {
    type: TypeKeys.GET_CATEGORY_PAGE_API_FAIL
  }

  return action
}

export const fetchCategoryPageData = ({
  location,
  apiAppUrlPrefix,
  locationParams
}: {
  location: ICommonLocation
  apiAppUrlPrefix: string
  locationParams: ILocationParams
}): ThunkAction<Promise<ActionTypes>, IRootState, void> => {
  return (dispatch, getState) => {
    const { currentSite } = getState().common
    const category = locationParams.category || 'all'

    dispatch(fetchCategoryPageDataRequest())

    return axiosVerbose
      .get<customerMarketShopfrontApi.categoryPage.IResult>(
        `${apiAppUrlPrefix}/shopfront-api/category_page/${currentSite}/${category}`
      )
      .then(response => {
        const data = {
          ...response.data,
          blocks: transformBlocksToArray(response.data.blocks),
          blocksB: transformBlocksToArray(response.data.blocksB),
          blocksC: transformBlocksToArray(response.data.blocksC),
          blocksD: transformBlocksToArray(response.data.blocksD)
        }

        if (data.metadata) {
          dispatch(setSeoMeta(data.metadata))
        }

        // Temporary, for purpose of A/B/C/D experiment
        const itemIdsForBookmarks = uniq(
          [data.blocks, data.blocksB, data.blocksC, data.blocksD]
            .map(getItemIdsFromBlocks)
            .reduce((acc, value) => acc.concat(value), [])
        )

        dispatch(getBookmarksForItems(itemIdsForBookmarks))

        return dispatch(fetchCategoryPageDataSuccess(data))
      })
      .catch(err => Promise.reject(logError(err)))
      .catch(() => {
        return dispatch(fetchCategoryPageDataFail())
      })
  }
}
